import {
    type OrthographicCamera,
    type Scene,
    WebGLRenderer,
    type WebGLRendererParameters,
} from 'three';
import { stopAll, type StopHandle } from '@/util';
import { type ElementSize, observeElementSize } from '@/util/observeElementSize';

export type BeforeRenderCallback = () => void;

/**
 * Default debounce for resize-observer callbacks. Matches the existing
 * `_VIEWPORT_UPDATE_DEBOUNCE_MS` in `updateViewport.ts`.
 */
const DEFAULT_RESIZE_DEBOUNCE_MS = 5;

/**
 * Default WebGLRenderer parameters used by every scene in the app.
 * Each call site can override via `rendererParams`.
 */
const DEFAULT_RENDERER_PARAMS: Omit<WebGLRendererParameters, 'canvas'> = {
    alpha: true,
    stencil: false,
    antialias: true,
};

export type ResizeCallback = (size: ElementSize) => void;

export type SceneRuntimeOptions = {
    container: HTMLElement;
    canvas: HTMLCanvasElement;
    scene: Scene;
    camera: OrthographicCamera;
    rendererParams?: Omit<WebGLRendererParameters, 'canvas'>;

    /** Defaults to {@link DEFAULT_RESIZE_DEBOUNCE_MS}. */
    resizeDebounceMs?: number;
};

export type SceneRuntime = {
    readonly renderer: WebGLRenderer;
    /** Register a callback to fire each frame, before `renderer.render()`. */
    beforeRender: (callback: BeforeRenderCallback) => void;
    /**
     * Subscribe to viewport resize events. Use this to update camera frustum
     * or other size-dependent scene state.
     *
     * If the runtime has already observed a size by the time this is called,
     * the callback fires synchronously with that last size. This lets callers
     * register after construction without missing the initial resize.
     */
    onResize: (callback: ResizeCallback) => void;
    /** Tear down the animation loop, resize observer, and dispose the renderer. */
    stop: StopHandle;
};

/**
 * Create the WebGL renderer, animation loop, and viewport-resize observer
 * shared by every Three.js scene in the app.
 *
 * The runtime deliberately does NOT manage clear color, local clipping,
 * camera frustum math, or controls — those vary per scene and live at the
 * call site, mutating `runtime.renderer` directly.
 */
export function createSceneRuntime(options: SceneRuntimeOptions): SceneRuntime {
    const {
        container,
        canvas,
        scene,
        camera,
        rendererParams,
        resizeDebounceMs = DEFAULT_RESIZE_DEBOUNCE_MS,
    } = options;

    const renderer = new WebGLRenderer({
        ...DEFAULT_RENDERER_PARAMS,
        ...rendererParams,
        canvas,
    });

    const beforeRenderCallbacks: BeforeRenderCallback[] = [];
    const resizeCallbacks: ResizeCallback[] = [];
    let lastSize: ElementSize | null = null;

    renderer.setAnimationLoop(() => {
        for (const callback of beforeRenderCallbacks) {
            callback();
        }
        renderer.render(scene, camera);
    });

    const stopResize = observeElementSize(
        container,
        (size) => {
            if (size.width !== canvas.width || size.height !== canvas.height) {
                renderer.setSize(size.width, size.height, false);
            }
            lastSize = size;
            for (const callback of resizeCallbacks) {
                callback(size);
            }
        },
        { debounce: resizeDebounceMs },
    );

    const stop = stopAll(
        stopResize,
        () => renderer.setAnimationLoop(null),
        () => renderer.dispose(),
    );

    return {
        renderer,
        beforeRender: (callback) => beforeRenderCallbacks.push(callback),
        onResize: (callback) => {
            resizeCallbacks.push(callback);
            if (lastSize) {
                callback(lastSize);
            }
        },
        stop,
    };
}
